import { inject, Injectable } from '@angular/core';
import { AuditDto, WarehouseDto } from '@pf/platform-wms/domain';
import { CycleCountTask } from '../models/cycle-count-task.model';
import { endOfDay, endOfToday, startOfDay } from 'date-fns';
import { CpwTaskStateService } from '../infrastructure/cpw-task-state.service';
import { groupBy, orderBy, uniqBy } from 'lodash-es';
import { HashObject } from '@pf/shared-utility';

const DEFAULT_BATCH_SIZE = 20;

@Injectable({
    providedIn: 'root',
})
export class CycleCountTaskOrchestratorService {
    private readonly taskStateService = inject(CpwTaskStateService);

    orchestrate(
        warehouse: WarehouseDto,
        newAudits: AuditDto[],
        activeAudits: AuditDto[]
    ) {
        const batchSize =
            warehouse.settings?.cycleCountBatchSize ?? DEFAULT_BATCH_SIZE;
        const tasks = this.mapTasks(
            batchSize,
            this.sortAudits(newAudits),
            this.sortAudits(activeAudits)
        );

        this.taskStateService.setTasks(tasks);
    }

    private sortAudits(audits: AuditDto[]) {
        return orderBy(
            audits,
            [
                'dueDate',
                'inventoryBundle.container.slot.zone.zonePriority', // Zone
                'inventoryBundle.container.slot.slotPriority', // Slot
            ],
            ['desc', 'asc', 'asc']
        );
    }

    private mapTasks(
        batchSize: number,
        newAudits: AuditDto[],
        activeAudits: AuditDto[]
    ) {
        const today = endOfToday();

        newAudits = orderBy(
            newAudits,
            ['dueDate', 'quantityBefore'],
            ['asc', 'asc']
        );

        const auditsDueToday = newAudits.filter(
            audit =>
                audit.dueDate &&
                startOfDay(new Date(audit.dueDate as string)) <= today
        );

        const auditsDueInFuture = newAudits.filter(
            audit =>
                !audit.dueDate ||
                endOfDay(new Date(audit.dueDate as string)) > today
        );

        const todayTasks = this.getAuditTasks(
            batchSize,
            'today',
            this.groupByAuditType(auditsDueToday)
        );

        const futureTasks = this.getAuditTasks(
            batchSize,
            'future',
            this.groupByAuditType(auditsDueInFuture)
        );

        const activeAuditTasks = this.getAuditTasks(
            batchSize,
            'active',
            this.groupByAuditType(activeAudits)
        );

        if (todayTasks.length > 0) {
            futureTasks.forEach(task => {
                task.readonly = true;
            });
        }

        if (activeAuditTasks.length > 0) {
            todayTasks.forEach(task => {
                task.readonly = true;
            });
            futureTasks.forEach(task => {
                task.readonly = true;
            });
            activeAuditTasks.forEach(task => {
                task.claimedAt = activeAudits[0].modifiedDate;
            });
        }

        return [...todayTasks, ...futureTasks, ...activeAuditTasks];
    }

    private groupByAuditType(audits: AuditDto[]) {
        return groupBy(audits, audit => audit.auditType?.name || 'Parts');
    }

    private getAuditTasks(
        batchSize: number,
        section: CycleCountTask['section'],
        auditGroups: Record<string, AuditDto[]>
    ) {
        const tasks: CycleCountTask[] = [];
        Object.entries(auditGroups).forEach(([group, audits]) => {
            audits
                .reduce((buckets, audit, index) => {
                    const groupIndex = Math.floor(index / batchSize);
                    if (!buckets[groupIndex]) buckets[groupIndex] = [];
                    buckets[groupIndex].push(audit);
                    return buckets;
                }, [] as AuditDto[][])
                .forEach(bucket => {
                    const task = this.mapAuditsToTask(section, bucket, group);
                    tasks.push(task);
                });
        });
        return tasks;
    }

    private mapAuditsToTask(
        section: 'priority' | 'today' | 'future' | 'active',
        audits: AuditDto[],
        group: string
    ) {
        const title =
            audits.length > 1
                ? `${audits.length} Cycle Counts`
                : `Cycle Count ${audits[0].item?.sku}`;
        const zones = uniqBy(
            audits.map(audit => audit.inventoryBundle?.container?.slot?.zone),
            x => x?.id as string
        );
        const slots = uniqBy(
            audits.map(audit => audit.inventoryBundle?.container?.slot),
            x => x?.id as string
        );
        const id = HashObject(audits.map(x => x.id).sort());
        return {
            id,
            section: section,
            priority: false,
            group,
            warehouseId: audits[0].warehouse!.id,
            time: audits[0].createdDate,
            details: `${slots.length} Slots in ${zones.length} Zones`,
            readonly: false,
            title,
            taskIconType: 'cycleCounts',
            audits,
            context: {
                auditIds: audits.map(x => x.id),
            },
        } as CycleCountTask;
    }
}
